import type { StateCreator } from "zustand";
import { MAX_ERRORS_PER_NODE } from "../constants";
import type { NodeErrorEntry } from "../types";
import type { RunControlState } from "./index";

export interface ErrorsSlice {
  nodeErrors: Map<string, NodeErrorEntry[]>;
  lastNodeError: NodeErrorEntry | null;
  addNodeError: (entry: NodeErrorEntry) => void;
  clearNodeError: (nodeId: string, frameId?: string) => void;
  clearAllErrors: () => void;
}

export const createErrorsSlice: StateCreator<
  RunControlState,
  [["zustand/immer", never], ["zustand/subscribeWithSelector", never]],
  [],
  ErrorsSlice
> = (set) => ({
  nodeErrors: new Map(),
  lastNodeError: null,

  addNodeError: (entry) =>
    set((state) => {
      const list = state.nodeErrors.get(entry.nodeId) ?? [];
      if (list.some((item) => item.id === entry.id)) {
        return;
      }
      list.push(entry);
      if (list.length > MAX_ERRORS_PER_NODE) {
        list.splice(0, list.length - MAX_ERRORS_PER_NODE);
      }
      state.nodeErrors.set(entry.nodeId, list);
      state.lastNodeError = entry;
    }),

  clearNodeError: (nodeId, frameId) =>
    set((state) => {
      const current = state.nodeErrors.get(nodeId);
      if (!current) return;

      if (!frameId) {
        state.nodeErrors.delete(nodeId);
      } else {
        const filtered = current.filter((entry) => entry.frameId !== frameId);
        if (filtered.length) {
          state.nodeErrors.set(nodeId, filtered);
        } else {
          state.nodeErrors.delete(nodeId);
        }
      }

      if (state.lastNodeError?.nodeId === nodeId) {
        const remaining = state.nodeErrors.get(nodeId);
        state.lastNodeError = remaining?.[remaining.length - 1] ?? null;
      }
    }),

  clearAllErrors: () =>
    set((state) => {
      state.nodeErrors = new Map();
      state.lastNodeError = null;
    }),
});
